import type { GetStaticPaths, GetStaticProps } from 'next'
import Link from 'next/link'
import { Blocks, CtaBar, Faq, RelatedLinks, Tldr } from '@/components/Seo/ContentBlocks'
import { GUIDES, Guide, findGuide } from '@/lib/seoContent'
import { BRAND, DEFAULT_OG_IMAGE, ORG_ID, absUrl, breadcrumbLd, faqLd, SeoProps } from '@/utils/seo'

const GuidePage = ({ guide }: { guide: Guide }) => (
  <div className="min-h-screen bg-gray-50">
    <div className="mx-auto max-w-3xl px-4 py-8 md:py-12">
      <nav aria-label="面包屑" className="mb-4 flex gap-1 text-sm text-gray-500">
        <Link href="/" className="text-gray-500 hover:text-gray-900">
          首页
        </Link>
        <span>/</span>
        <Link href="/guide" className="text-gray-500 hover:text-gray-900">
          办赛指南
        </Link>
      </nav>
      <article className="flex flex-col gap-8 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5 md:p-10">
        <header className="flex flex-col gap-3">
          <h1 className="text-3xl font-black leading-tight text-gray-900">{guide.h1}</h1>
          <div className="text-xs text-gray-400">
            {BRAND} 赛事团队 · 更新于 <time dateTime={guide.updated}>{guide.updated}</time>
          </div>
          <Tldr text={guide.tldr} />
        </header>
        <Blocks blocks={guide.blocks} />
        {guide.howto && (
          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-bold text-gray-900">{guide.howto.name}</h2>
            <ol className="flex list-decimal flex-col gap-1.5 pl-6 text-gray-700">
              {guide.howto.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          </section>
        )}
        <Faq faq={guide.faq} />
        <CtaBar />
        <RelatedLinks links={guide.related} />
      </article>
    </div>
  </div>
)

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: GUIDES.map((g) => ({ params: { slug: g.slug } })),
  fallback: false,
})

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const guide = findGuide(String(params?.slug))
  if (!guide) return { notFound: true }
  const path = `/guide/${guide.slug}`
  const seo: SeoProps = {
    title: guide.title,
    description: guide.description,
    keywords: guide.keywords,
    path,
    type: 'article',
    modifiedTime: guide.updated,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: guide.h1,
        description: guide.description,
        image: [DEFAULT_OG_IMAGE],
        dateModified: guide.updated,
        datePublished: guide.updated,
        author: { '@type': 'Organization', '@id': ORG_ID, name: BRAND, url: absUrl('/about_us') },
        publisher: { '@id': ORG_ID },
        mainEntityOfPage: absUrl(path),
        inLanguage: 'zh-CN',
        abstract: guide.tldr,
      },
      ...(guide.howto
        ? [
            {
              '@context': 'https://schema.org',
              '@type': 'HowTo',
              name: guide.howto.name,
              step: guide.howto.steps.map((text, i) => ({ '@type': 'HowToStep', position: i + 1, text })),
            },
          ]
        : []),
      faqLd(guide.faq),
      breadcrumbLd([
        ['首页', '/'],
        ['办赛指南', '/guide'],
        [guide.h1, path],
      ]),
    ],
  }
  return { props: { guide, seo } }
}

export default GuidePage
